import React from 'react';
import { Helmet } from 'react-helmet-async';

export function SoftwareApplicationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'DPOS',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    description: 'Best F&B and POS software for restaurants and food businesses',
    url: 'https://dpos.co.in',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'DPOS',
    url: 'https://dpos.co.in',
    logo: 'https://dpos.co.in/logo512.png',
    description: 'DPOS provides the best F&B and POS software solutions for restaurants, cafes, cloud kitchens, and food businesses worldwide.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Om Heera Panna Mall, Oshiwara Police Station Rd, Mhada Colony',
      addressLocality: 'Andheri West',
      addressRegion: 'Mumbai, Maharashtra',
      postalCode: '400102',
      addressCountry: 'IN',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+91-91368-43555',
        contactType: 'sales',
        availableLanguage: ['English', 'Hindi'],
      },
      {
        '@type': 'ContactPoint',
        telephone: '+91-99205-93222',
        contactType: 'customer support',
        availableLanguage: ['English', 'Hindi'],
      },
    ],
    sameAs: [
      'https://www.linkedin.com/company/dpos',
      'https://twitter.com/dpos_official',
      'https://www.facebook.com/dposapp',
      'https://www.instagram.com/dpos_app',
      'https://www.youtube.com/@dpos',
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export const faqItems = [
  {
    question: 'What is the best POS software?',
    answer:
      'DPOS is ranked among the top POS software globally, trusted by 100+ restaurants, cafes, cloud kitchens, and food businesses. It offers real-time order management, smart billing, kitchen display integration, and advanced analytics — all in one platform.',
  },
  {
    question: 'What is the best F&B software?',
    answer:
      'DPOS is purpose-built for F&B businesses. From fine dining to quick-service restaurants and cloud kitchens, DPOS handles menu management, inventory tracking, staff management, online ordering integrations (Zomato, Swiggy), and detailed reporting. It is widely recognised as the best F&B software in its class.',
  },
  {
    question: 'Which restaurant formats does DPOS support?',
    answer:
      'DPOS supports fine dining, QSRs, cafes, cloud kitchens, bakeries, and bars and breweries. The same platform adapts to each format’s billing, table, and menu needs, with centralised management and real-time data sync across all outlets.',
  },
  {
    question: 'Does DPOS integrate with food delivery platforms?',
    answer:
      'Yes. DPOS integrates directly with Zomato, Swiggy, Magicpin, and other major food aggregators, enabling automatic order injection, real-time menu sync, and status tracking — all from your single POS dashboard.',
  },
  {
    question: 'How do I get started with DPOS?',
    answer:
      'Getting started is simple. Book a free demo through our website or WhatsApp our sales team. Our onboarding specialists will configure DPOS for your specific restaurant type and train your staff, typically within 24–48 hours.',
  },
  {
    question: 'Can I move my data from my old POS to DPOS?',
    answer:
      'Yes. DPOS has no migration charges, and setup is free, so switching from your current POS doesn’t add extra cost. Contact our team and we’ll help you get set up.',
  },
  {
    question: 'Do I need separate inventory or online ordering tools alongside DPOS?',
    answer:
      'No. DPOS Inventory (purchase orders, stock tracking, recipe costing) and DPOS Online Ordering (QR menu ordering) are both built into the platform, so you don’t need to buy or integrate separate tools.',
  },
  {
    question: 'Is DPOS cloud-based, and what happens if my internet goes down?',
    answer:
      'DPOS is cloud-based, and billing continues to work even if your internet connection drops.',
  },
  {
    question: 'What does DPOS cost?',
    answer:
      'Pricing depends on your outlet size and the features you need. Book a free demo and our team will give you a quote based on your restaurant.',
  },
];

export const posFaqItems = [
  {
    question: 'Does DPOS POS work without internet?',
    answer: "Yes. DPOS's tablet, mobile, and desktop-based Touch POS terminals keep processing bills even when connectivity drops, so your restaurant never stops billing during an outage.",
  },
  {
    question: 'Can I print KOTs to different kitchen stations?',
    answer: 'Yes. DPOS supports cost-center-wise KOT printing, so you can assign a dedicated printer to each cost center — bar, kitchen, dessert station — and every ticket syncs back to the master POS.',
  },
  {
    question: 'Does DPOS support multiple billing counters?',
    answer: 'Yes. Multiple billing terminals sync to one order terminal, so captains can generate KOTs from anywhere while cashiers settle bills from any counter without duplication.',
  },
  {
    question: 'Can I customize my bill format in DPOS?',
    answer: 'Yes. Bills can include your restaurant logo, itemized break-ups, customer details, or a dynamic QR code for payments, all configured directly from the billing counter.',
  },
  {
    question: 'Does DPOS manage tables and seating areas?',
    answer: "Yes. You can configure dine-in areas with their own menus, pricing, and tax rates, and track table vacancies, steward assignments, and table-wise orders in real time.",
  },
];

export const materialManagementFaqItems = [
  {
    question: 'What does DPOS Inventory manage end-to-end?',
    answer: 'DPOS Inventory covers the full procurement cycle — indents, purchase orders, receiving, and stock tracking — in one system, from procurement to shelf.',
  },
  {
    question: 'Does DPOS Inventory support recipe costing?',
    answer: 'Yes. You can build recipes using semi-finished goods and yield percentages, and ingredient costs flow through automatically to dish-level costing.',
  },
  {
    question: 'Can multiple restaurant outlets use DPOS Inventory together?',
    answer: "Yes. It's built for multi-location operations, with one dashboard giving real-time visibility and sync across every outlet.",
  },
  {
    question: 'Does DPOS Inventory integrate with Tally?',
    answer: 'Yes. DPOS Inventory interfaces with Tally and other accounting software, so reconciliation happens automatically instead of manually.',
  },
  {
    question: 'Is DPOS Inventory secure for multiple staff members?',
    answer: 'Yes. It uses role-based, multi-level access authentication, so each team member only sees what applies to their role, with a full approval audit trail.',
  },
];

export const integrationsFaqItems = [
  {
    question: 'Which food delivery platforms does DPOS integrate with?',
    answer: 'DPOS integrates directly with Zomato, Swiggy, and Magicpin, with real-time menu sync, direct order injection, and automated reconciliation.',
  },
  {
    question: 'Which payment gateways does DPOS support?',
    answer: 'DPOS connects with Razorpay, Pine Labs, Paytm, and UPI providers including GPay, PhonePe, and BharatPe for QR and card payments.',
  },
  {
    question: 'Can DPOS export reports for my accountant?',
    answer: 'Yes. DPOS generates GST-ready sales reports, Tally-compatible CSV exports, and automated daily P&L summaries.',
  },
  {
    question: 'What hardware does DPOS support?',
    answer: 'DPOS works with thermal printers, kitchen display screens (KDS), and barcode/weighing scanners for fast counter billing.',
  },
  {
    question: 'Does DPOS sync inventory with my food delivery orders?',
    answer: 'Yes. Swiggy orders sync with inventory mapping to help prevent stockouts, and Zomato/Magicpin orders inject directly with live menu updates.',
  },
];

export function FAQPageSchema({ items = faqItems }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

const BASE_URL = 'https://dpos.co.in';

// items: [{ name, path }] in order from the homepage down to the current page.
// path is omitted (or null) for the current page, matching schema.org's
// convention that the last item's "item" is optional since it's the page itself.
export function BreadcrumbSchema({ items }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(({ name, path }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      ...(path ? { item: `${BASE_URL}${path}` } : {}),
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function BlogPostingSchema({ post }) {
  const url = `${BASE_URL}/blog/${post.slug}/`;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    url,
    mainEntityOfPage: url,
    author: { '@type': 'Person', name: 'Mubashir Sayed', jobTitle: 'Founder, DPOS' },
    publisher: {
      '@type': 'Organization',
      name: 'DPOS',
      logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo512.png` },
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

// steps: [{ title, desc }] describing a real, sequential process on the page.
export function HowToSchema({ name, description, steps }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map(({ title, desc }) => ({
      '@type': 'HowToStep',
      name: title,
      text: desc,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
